import {computed, makeObservable} from "mobx";
import {LineupsMemberViewModel} from "../../../domain/yard/lineups/lineups-member/lineups-member-view-model";
import {EventViewModel} from "../../../domain/common/events/models/event-view-model";
import {EventTypeViewModel} from "../../../domain/common/events/models/event-type-view-model";
import {SOCCER_EVENT_TYPES} from "../../../core/app/consts";
import {LineupsMemberDomainModel} from "../../../domain/yard/lineups/lineups-member/lineups-member-domain-model";
import {LINEUPS} from "../lineups-widget.consts";
import {CompetitorViewModel} from "../../../domain/common/competitor/models/competitor-view-model";
import {ATHLETE_WIDGET} from "../../athlete-widget/athlete-widget-consts";
import {CompetitorLogoParams} from "../../../domain/common/competitor/models/competitor-logo-params";
import {EntityLogoRequestParams} from "../../../infrastructure/models/params/entity-logo-request-params";
import {CountryFlagParams} from "../../../domain/common/country/models/country-flag-params";

export class LineupsWidgetMemberViewModel extends LineupsMemberViewModel {
  domainModel: LineupsMemberDomainModel;
  returnStatusTerm: String;
  isPreGame: String;
  isNationalTeam: String;
  assists: Number;

  constructor(model: LineupsMemberDomainModel, returnStatusTerm: String, isPreGame: Boolean, isNationalTeam: Boolean, assists: Number = 0) {
    super(model);

    this.returnStatusTerm = returnStatusTerm;
    this.isPreGame = isPreGame;
    this.isNationalTeam = isNationalTeam;
    this.assists = assists;

    makeObservable(this);
  }

  @computed get shortName(): String {
    return this.domainModel.shortName || this.domainModel.name;
  }

  @computed get card(): EventViewModel {
    const redCardEvent: EventViewModel = this.events && this.events.find((event) => event.eventType.id === SOCCER_EVENT_TYPES.redCard);
    const yellowCardEvent: EventViewModel = this.events && this.events.find((event) => event.eventType.id === SOCCER_EVENT_TYPES.yellowCard);

    return redCardEvent || yellowCardEvent;
  }

  @computed get suspensionRiskCard() {
    if (this.isPreGame && this.suspensionRisk) {
      return {
        eventType: new EventTypeViewModel({
          id: LINEUPS.EVENT_TYPES.yellowCard,
          subTypeId: 0,
          name: 'Yellow Card'
        })
      };
    }
    return null;
  }

  @computed get switchEvent(): EventViewModel {
    const switchEvent: EventViewModel = this.events.find((event: EventViewModel) => event.eventType.id === LINEUPS.EVENT_TYPES.substitution);

    return switchEvent;
  }

  @computed get goals(): Number {
    const goals: Number = this.events && this.events.filter((event: EventViewModel) => {
      return event.eventType.id === SOCCER_EVENT_TYPES.goal && event.eventType.subTypeId === LINEUPS.GOAL_SUB_TYPES.fieldGoal
    }).length;

    return goals;
  }

  @computed get selfGoals(): Number {
    const selfGoals: Number = this.events && this.events.filter((event: EventViewModel) => {
      return event.eventType.id === SOCCER_EVENT_TYPES.goal && event.eventType.subTypeId === LINEUPS.GOAL_SUB_TYPES.ownGoal
    }).length;

    return selfGoals;
  }

  @computed get totalGoals(): Number {
    return this.goals + this.penalties;
  }

  @computed get penalties(): Number {
    const penalties: Number = this.events && this.events.filter((event: EventViewModel) => {
      return event.eventType.id === SOCCER_EVENT_TYPES.goal && event.eventType.subTypeId === LINEUPS.GOAL_SUB_TYPES.penalty
    }).length;

    return penalties;
  }

  @computed get positionName(): String {
    const substitutionEvent = !this.isStarting && this.events?.find((event: EventViewModel) => event.eventType.id === SOCCER_EVENT_TYPES.substitution);
    const positionName: String = this.formation?.name || this.position?.name;
    let title: String = substitutionEvent ? substitutionEvent.extraPlayers[0].name : positionName;

    return title;
  }

  @computed get missingReason(): String {
    let missingReason: String = null;

    if (this.injury?.reason) {
      missingReason = this.injury.reason;
    } else if (this.suspension?.name) {
      missingReason = this.suspension.name;
    }

    return missingReason;
  }

  @computed get tooltipTitle(): String {
    let title: String = null;
    if (this.injury.expectedReturn || this.missingReason) {
      const returnTerm: String = this.returnStatusTerm.format({RETURN: this.injury.expectedReturn});
      title = `${this.missingReason ? `${this.missingReason}` : ''} ${this.injury?.expectedReturn ? `(${returnTerm})` : ''}`;
    }

    return title;
  }

  get isHotStreak(): Boolean {
    const isHotStreak = this.goals + this.penalties > 2;

    return isHotStreak;
  }

  get isAssistHotStreak(): Boolean {
    return this.assists > 2;
  }

  get isGoalKeeper(): Boolean {
    const isGoalKeeper = (this.yardFormation && this.yardFormation.fieldPosition === 1) || this.positionName === 'Goalkeeper';

    return isGoalKeeper;
  }

  get isStarting(): Boolean {
    return this.status === LINEUPS.MEMBERS_STATUS.starting;
  }

  @computed get rankingClassName(): String {
    let rankingClassName: String = LINEUPS.RATING_CLASS_NAMES.noRating;

    if (this.hasHighestRanking) {
      rankingClassName = LINEUPS.RATING_CLASS_NAMES.best
    } else if (this.ranking <= LINEUPS.RATING_TYPES.low) {
      rankingClassName = LINEUPS.RATING_CLASS_NAMES.low
    } else if (this.ranking <= LINEUPS.RATING_TYPES.moderate) {
      rankingClassName = LINEUPS.RATING_CLASS_NAMES.moderate
    } else if (this.ranking <= LINEUPS.RATING_TYPES.medium) {
      rankingClassName = LINEUPS.RATING_CLASS_NAMES.medium
    } else if (this.ranking >= LINEUPS.RATING_TYPES.high) {
      rankingClassName = LINEUPS.RATING_CLASS_NAMES.high
    }

    return rankingClassName;
  }

  @computed get isSubstitute(): Boolean {
    const isSubstitute: Boolean = this.status === LINEUPS.MEMBERS_STATUS.substitute;

    return isSubstitute;
  }

  @computed get hasPlayerCard(): Boolean {
    const hasPlayerCard: Boolean = this.athleteId > 0 && this.hasStats && (this.isStarting || this.isSubstitute || this.gameId);

    return hasPlayerCard;
  }

  @computed get playerStatus(): String {
    let playerStatus: String = '';

    if (this.isStarting) {
      playerStatus = LINEUPS.ANALYTICS.PLAYER_STATUS.lineups;
    } else if (this.isSubstitute) {
      playerStatus = LINEUPS.ANALYTICS.PLAYER_STATUS.bench;
    }

    return playerStatus;
  }

  @computed get isSubstitutionIn(): Boolean {
    const isSubstitutionIn: Boolean = this.substitutionEvent && !this.isStarting;

    return isSubstitutionIn
  }

  @computed get isSubstitutionOut(): Boolean {
    const isSubstitutionOut: Boolean = this.substitutionEvent && !this.isSubstitute;

    return isSubstitutionOut
  }

  @computed get shouldRenderJerseyNumber(): Boolean {
    const shouldRenderJerseyNumber: Boolean = this.isStarting || this.isSubstitute;

    return shouldRenderJerseyNumber;
  }

  get competitor(): CompetitorViewModel {
    let competitor: CompetitorViewModel = null;
    if (this.domainModel.competitor) {
      competitor = new CompetitorViewModel(this.domainModel.competitor);
    }
    return competitor;
  }

  @computed get shouldRenderPlayerTeamLogo(): Boolean {
    const shouldRenderPlayerTeamLogo: Boolean = this.competitor;

    return shouldRenderPlayerTeamLogo;
  }

  @computed get isCoach(): Boolean {
    return this.formation?.id === ATHLETE_WIDGET.FORMATION_ID.coach;
  }

  @computed get shouldRenderTeamOrCountryLogo(): Boolean {
    const shouldRenderPlayerTeamLogo: Boolean = this.shouldRenderPlayerTeamLogo || this.iconLogoSrc;

    return shouldRenderPlayerTeamLogo;
  }

  @computed get iconLogoSrc(): EntityLogoRequestParams {
    return this.isPreGame ? this.formationViewLogoSrc : null;
  }

  /**
   * Logo shown for the "Nationality" toggle: the player's country flag for domestic games,
   * or the player's club crest for international (national-team) games. Unlike `iconLogoSrc`
   * it is not gated to pre-game, so it is available during live/post games as well.
   */
  @computed get formationViewLogoSrc(): EntityLogoRequestParams {
    let formationViewLogoSrc = null;
    let iconSize = this.isStarting ? LINEUPS.FORMATION_STATUS_ICON_SIZE : LINEUPS.PLAYERS_LIST_ICON_SIZE;
    let iconEntityId: Number = this.nationalId;
    let IconLogoType = CountryFlagParams;
    if (this.isNationalTeam && this.competitorId > 0) {
      iconEntityId = this.domainModel.lineupsMemberApiModel.competitorId
      IconLogoType = CompetitorLogoParams;
      iconSize = this.isStarting ? LINEUPS.FORMATION_COMPETITOR_ICON_SIZE : iconSize;
    }
    if (iconEntityId > 0 && IconLogoType) {
      formationViewLogoSrc = new IconLogoType({width: iconSize, height: iconSize, ratio: 3}, {id: iconEntityId});
    }

    return formationViewLogoSrc;
  }

  @computed get shouldRenderPlayerJerseyNum(): Boolean {
    const shouldRenderPlayerJerseyNum: Boolean =  !this.competitor && !this.isCoach;

    return shouldRenderPlayerJerseyNum;
  }

  @computed get yellowCardEvent() {
    return this.events?.find((event: EventViewModel) => event.eventType.id === LINEUPS.EVENT_TYPES.yellowCard);
  }
}

